import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { Subject, takeUntil } from 'rxjs';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { Item, Items } from './file-manager.types';
import { cloneDeep } from "lodash-es";
import { ToastrService } from 'ngx-toastr';
import { FuseConfirmationService } from "@fuse/services/confirmation";
import { UsersService } from 'app/pages/settings/users/users.service';
import _ from 'lodash';
import { environment } from 'environments/environment';
import { FileManagerService } from './file-manager.service';

@Component({
    selector: 'file-manager',
    templateUrl: './file-manager.component.html',
    styleUrls: ['./file-manager.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileManagerComponent implements OnInit, OnDestroy {
    @ViewChild('matDrawer', { static: true }) matDrawer: MatDrawer;
    @Input() viewMode: boolean = false;
    drawerMode: 'side' | 'over';
    selectedItem: Item;
    insideFolder: boolean = false;
    @Input() items: Items;
    @Output() detectChange = new EventEmitter();
    @Output() deleteChange = new EventEmitter();
    @Output() archive = new EventEmitter();
    @Output() refreshItems = new EventEmitter();
    copyItems: Items;
    openDrawer: boolean = false;
    item: Item;
    folderName: string = '';
    userDesignations: any[] = []
    backupCalled: boolean = false;
    itemsRefreshed: boolean = false;
    @Input() upload: boolean = true;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private toastr: ToastrService,
        private _fuseConfirmationService: FuseConfirmationService,
        private _userService: UsersService,
        private _fileManagerService: FileManagerService
    ) {
    }


    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnChanges(changes: SimpleChanges) {
        if (changes['items']) {
            const { files, folders } = this.items;
            //Keep Original Copy
            this.copyItems = cloneDeep(this.items);

            //Filter Root Dir Files
            for (let i = 0; i < files.length; i++) {
                if (files[i].folderId !== null && files[i].folderId !== "otherDocuments") {
                    this.items.files.splice(i, 1);
                    i--; // adjust index since array length has changed
                }
            }
            if (!this.selectedItem) {
                //Drawer item initilization
                this.item = files[0] || folders[0]
            }
            else {
                this.openFolder(this.selectedItem)
            }
            if (!_.isEqual(changes['items'].previousValue, changes['items'].currentValue)) {
                this.refreshItems.emit()
            }
        }
    }
    /**
     * On init
     */
    ngOnInit(): void {
        this._fuseMediaWatcherService.onMediaQueryChange$('(min-width: 1440px)')
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((state) => {

                // Calculate the drawer mode
                this.drawerMode = state.matches ? 'side' : 'over';

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
        this._userService.getUserDesignations()
            .subscribe((response: any) => {
                this.userDesignations = response.designations;
            })


    }

    /**
     * On Destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * On backdrop clicked
     */
    onBackdropClicked(): void {
        // Go back to the list
        this.openHome();

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    openHome() {
        this.insideFolder = false;
        this.items.files = [...this.copyItems.files]
        this.items.folders = [...this.copyItems.folders]
        Object.keys(this.copyItems.folders).forEach((key) => {
            const filesData = this.copyItems.files.filter(item => {
                return item.folderId === this.copyItems.folders[key]["id"]
            });
            const fileLength = filesData.length
            this.items.folders[key].contents = fileLength ? fileLength + ' file(s)' : 0 + ' file(s)'
        });
        //Filter Root Dir Files
        const { files } = this.items;
        for (let i = 0; i < files.length; i++) {
            if (files[i].folderId !== null && files[i].folderId !== "otherDocuments") {
                this.items.files.splice(i, 1);
                i--; // adjust index since array length has changed
            }
        }
        this.folderName = '';
        this.selectedItem = undefined;
    }

    async openFolder(folderId) {
        if ((!this.items || this.copyItems.files.length === 0) && !this.backupCalled) {
            await this.refreshItems.emit()
            this.backupCalled = true;
            this.openFolder(folderId)
        }
        this.selectedItem = folderId;
        const files = this.copyItems.files.filter(item => {
            return item.folderId === folderId
        });
        this.items.folders = [];
        this.items.files = [...files];
        this.insideFolder = true;

        //Get folder name
        this.folderName = this.copyItems.folders.filter(item => {
            return item.id === folderId;
        })[0].name;
    }

    selectFolder(folderId) {
        this.selectedItem = folderId;
        this.openDrawer = true;
        const folder = this.copyItems.folders.filter(file => file.id == folderId)
        if (folder.length > 0) {
            this.item = folder[0]
        }
    }

    selectFile(fileId, folderId) {
        this.selectedItem = fileId;
        this.openDrawer = true;
        const file = this.copyItems.files.filter(file => file.id == fileId && file.folderId === folderId)
        if (file.length > 0) {
            this.item = file[0]
        }
    }

    deleteStateSelector(fileId) {
        const file = this.items.files.filter(file => file.id == fileId);
        if (file[0]["folderId"] !== "Archived Documents") {
            this.archiveFile(fileId);
        }
        else {
            this.deleteFile(fileId);
        }

    }

    archiveFile(fileId) {
        const file = this.items.files.filter(file => file.id == fileId);
        if (file.length > 0) {
            const verify = this._fuseConfirmationService.open({
                title: 'Archive File',
                message: 'Are you sure you want to archive this file?',
                actions: {
                    confirm: {
                        label: 'Archive',
                        color: "primary",
                    }
                },
                icon: {
                    name: 'archive',
                    color: 'primary'
                }
            });
            verify.afterClosed().subscribe((result) => {

                // If the confirm button pressed...
                if (result === 'confirmed') {
                    this.copyItems.files = this.copyItems.files.filter(file => file.id !== fileId);
                    this.archive.emit(file[0])
                    this.openDrawer = false;
                }
            })

        }
    }

    deleteFile(fileId) {
        const file = this.items.files.filter(file => file.id == fileId);
        if (file.length > 0) {
            const verify = this._fuseConfirmationService.open({
                title: 'Delete File',
                message: 'Are you sure you want to delete this file?',
                actions: {
                    confirm: {
                        label: 'Delete',
                        color: "warn",
                    }
                },
                icon: {
                    name: 'feather:trash-2',
                    color: 'primary'
                }
            });
            verify.afterClosed().subscribe((result) => {

                // If the confirm button pressed...
                if (result === 'confirmed') {
                    this.copyItems.files = this.copyItems.files.filter(file => file.id !== fileId);
                    this.openDrawer = false;
                    this.deleteChange.emit(file[0]);
                }
            })

        }
    }

    uploadFile(event) {
        const { files } = event.target;
        const payload = [];
        for (let file of files) {

            //Check duplicate file exist
            const existingFileIndex = this.items.files.findIndex(
                (item) => item.name === file.name && item.folderId === (this.folderName || "otherDocuments")
            );
            if (existingFileIndex !== -1) {
                this.toastr.error(file.name + " file already exist");
                continue;
            }
            //Create object and push
            const fileObject = {
                id: file.lastModified,
                folderId: this.folderName || "otherDocuments",
                name: file.name,
                size: this.bytesToSize(file.size),
                description: file.name,
                type: (file.name.split(".")[1]).toUpperCase(),
                createdAt: file.lastModified,
                modifiedAt: file.lastModified,
            }

            this.items.files.push(fileObject);
            this.copyItems.files.push(fileObject);

            payload.push({
                file,
                folderName: this.folderName || "otherDocuments"
            });
        }
        //Push to parent
        if (payload.length !== 0) {
            this.detectChange.emit(payload);
        }
    }

    bytesToSize(bytes) {
        var sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        if (bytes == 0) return 'n/a';
        var i = Math.floor(Math.log(bytes) / Math.log(1024));
        if (i == 0) return bytes + ' ' + sizes[i];
        return (bytes / Math.pow(1024, i)).toFixed(1) + ' ' + sizes[i];
    };

    checkDesignation() {
        return this.userDesignations.find((designation) => designation === 'Super Admin')
    }

    isViewMode() {
        return this.viewMode;
    }

    filesDropped(event: any) {
        let files = { target: {} };
        files.target = { files: [event[0].file] };
        this.uploadFile(files)
    }

    downloadFile(filename: string) {
        let encodedName = encodeURIComponent(filename);
        this._fileManagerService.download(encodedName).subscribe((blob: Blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        });
    }

    getTypeColor(type: string): string {
        if (!type) {
            return 'bg-gray-600';
        }

        const upperType = type.toUpperCase();

        // Handle MIME types
        if (upperType.includes('WORDPROCESSINGML') || upperType.includes('MS-WORD') || upperType === 'MSWORD') {
            return 'bg-blue-600';
        }
        if (upperType.includes('SPREADSHEETML') || upperType.includes('MS-EXCEL')) {
            return 'bg-green-600';
        }
        if (upperType.includes('PRESENTATIONML') || upperType.includes('MS-POWERPOINT')) {
            return 'bg-orange-700';
        }

        switch (upperType) {
            case 'PDF':
                return 'bg-red-600';
            case 'DOC':
            case 'DOCX':
                return 'bg-blue-600';
            case 'XLS':
            case 'XLSX':
                return 'bg-green-600';
            case 'CSV':
                return 'bg-emerald-600';
            case 'JPG':
            case 'JPEG':
            case 'PNG':
            case 'GIF':
            case 'SVG':
                return 'bg-amber-600';
            case 'ZIP':
            case 'RAR':
            case '7Z':
                return 'bg-purple-600';
            case 'TXT':
                return 'bg-gray-600';
            case 'MP3':
            case 'WAV':
                return 'bg-pink-600';
            case 'MP4':
            case 'MOV':
                return 'bg-orange-600';
            case 'PPT':
            case 'PPTX':
                return 'bg-orange-700';
            case 'FOLDER':
                return 'bg-indigo-600';
            default:
                return 'bg-gray-600';
        }
    }

    /**
     * Get a friendly display type from the file type/MIME type
     * Maps long MIME types to short, readable extensions
     */
    getDisplayType(type: string): string {
        if (!type) {
            return 'FILE';
        }

        const upperType = type.toUpperCase();

        // MIME type mappings for Office documents
        const mimeTypeMap: { [key: string]: string } = {
            // Word documents
            'VND.OPENXMLFORMATS-OFFICEDOCUMENT.WORDPROCESSINGML.DOCUMENT': 'DOCX',
            'VND.MS-WORD': 'DOC',
            'MSWORD': 'DOC',
            // Excel documents
            'VND.OPENXMLFORMATS-OFFICEDOCUMENT.SPREADSHEETML.SHEET': 'XLSX',
            'VND.MS-EXCEL': 'XLS',
            // PowerPoint documents
            'VND.OPENXMLFORMATS-OFFICEDOCUMENT.PRESENTATIONML.PRESENTATION': 'PPTX',
            'VND.MS-POWERPOINT': 'PPT',
            // Other common MIME types
            'VND.OASIS.OPENDOCUMENT.TEXT': 'ODT',
            'VND.OASIS.OPENDOCUMENT.SPREADSHEET': 'ODS',
            'VND.OASIS.OPENDOCUMENT.PRESENTATION': 'ODP',
            'PLAIN': 'TXT',
            'X-ZIP-COMPRESSED': 'ZIP',
            'X-RAR-COMPRESSED': 'RAR',
            'X-7Z-COMPRESSED': '7Z',
        };

        // Check if it's a known MIME type
        if (mimeTypeMap[upperType]) {
            return mimeTypeMap[upperType];
        }

        // If type is already a short extension (like PDF, DOCX, etc.), return as-is
        if (upperType.length <= 5) {
            return upperType;
        }

        // For other long types, return first 4 characters + '...' or just the type if short enough
        return upperType.length > 8 ? upperType.substring(0, 4) + '…' : upperType;
    }


}
